/* eslint-disable no-unused-vars */
import { Table } from 'antd'
import clsx from 'clsx'
import dayjs, { Dayjs } from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import isoWeek from 'dayjs/plugin/isoWeek'
import React from 'react'
import { CALENDAR_TYPE, DAYS_OF_WEEK, HOURS_PER_DAY } from '../../../constants'
import { EventsByDay, ICell, IEvent, INorm, ITableColumn } from '../../../types'
import {
  calculateNormOfWeek,
  checkIsAllday,
  daysOfWeekKeys
} from '../../../utils'
import styles from '../SAPPCalendar.module.css'
import WeeklyCell from './WeeklyCell'
import { useDeviceType } from 'src/hooks/use-device-type'

dayjs.extend(isBetween)
dayjs.extend(isoWeek)

export interface IWeeklyCalendarProps {
  startDate: Dayjs
  endDate: Dayjs
  events: IEvent[]
  norms: INorm[]
  showWeeklyNorm: boolean
  onOpenDetail?: (date: Date, events: IEvent[]) => void
  onOpenCreate?: (date: Date) => void
  onEventDetail?: (event: IEvent) => void
  loading?: boolean
  type?: 'LMS' | 'OPS'
  eventDetail?: {event: IEvent | null, date: Dayjs} | null
  setEventDetail?: (params: {event: IEvent | null, date: Dayjs}) => void
}

const getEventAllDay = (events: IEvent[], targetDate: Dayjs) => {
  const allDayEvents = events.filter(e => e.isAllDay || e.type === 'HOLIDAY');

  const groupedById: Record<string, IEvent[]> = {};
  allDayEvents.forEach(event => {
    if (!groupedById[event.id]) groupedById[event.id] = [];
    groupedById[event.id].push(event);
  });

  const spanDaysMap: Record<string, Record<string, number>> = {};
  const idToMaxSpan: Record<string, number> = {};
  let maxSpan = 0;
  let maxSpanId: string | null = null;

  Object.entries(groupedById).forEach(([id, events]) => {
    const sorted = events.sort((a, b) =>
      dayjs(a.startDate).unix() - dayjs(b.startDate).unix()
    );

    let i = 0;
    while (i < sorted.length) {
      let start = i;
      let end = i;

      while (
        end + 1 < sorted.length &&
        dayjs(sorted[end + 1].startDate).startOf('day').diff(
          dayjs(sorted[end].startDate).startOf('day'),
          'day'
        ) === 1
      ) {
        end++;
      }

      const span = end - start + 1;
      const startKey = dayjs(sorted[start].startDate).format('YYYY-MM-DD');
      if (!spanDaysMap[id]) spanDaysMap[id] = {};
      spanDaysMap[id][startKey] = span;

      if (!idToMaxSpan[id] || span > idToMaxSpan[id]) {
        idToMaxSpan[id] = span;
      }

      if (span > maxSpan) {
        maxSpan = span;
        maxSpanId = id;
      }

      i = end + 1;
    }
  });

  return daysOfWeekKeys.reduce((acc: EventsByDay, day, index) => {
    const currentDate = targetDate.add(index, 'day');
    const dayKey = currentDate.format('YYYY-MM-DD');

    const maxSpanAllowed = 7 - index; // ví dụ Chủ nhật = 1, T7 = 2, v.v.

    const eventsForDay: IEvent[] = events.reduce((result: IEvent[], e) => {
      const isAllDay = e.isAllDay || e.type === 'HOLIDAY';
      const isSameDay = dayjs(e.startDate).isSame(currentDate, 'day');

      if (isAllDay && isSameDay) {
        const eventKey = dayjs(e.startDate).format('YYYY-MM-DD');
        let spanDays = spanDaysMap[e.id]?.[eventKey];

        // Apply maxSpanAllowed logic
        if (spanDays !== undefined) {
          spanDays = Math.min(spanDays, maxSpanAllowed);
        }

        result.push({
          ...e,
          isAllDay: true,
          spanDays: spanDays ?? undefined
        });
      }

      return result;
    }, []);

    const sortedEvents = eventsForDay.sort((a, b) => {
      const aMax = idToMaxSpan[a.id] || 0;
      const bMax = idToMaxSpan[b.id] || 0;

      if (aMax !== bMax) return bMax - aMax; // span dài hơn lên trước
      return a.id.localeCompare(b.id); // fallback: theo id tăng dần
    });

    acc[day] = {
      date: currentDate,
      events: sortedEvents
    };

    return acc;
  }, {} as EventsByDay);
};




const getEventsOfDay = (events: IEvent[], targetHour: Dayjs) =>
  daysOfWeekKeys.reduce((acc: EventsByDay, day, index) => {
    const currentDate = targetHour.add(index, 'day')

    acc[day] = {
      events: events.filter(
        (event) =>
          dayjs(event.startDate).isSame(currentDate, 'hour') &&
          !checkIsAllday(dayjs(event.startDate), dayjs(event.endDate))
      ),
      date: currentDate
    }
    return acc
  }, {} as EventsByDay)

const WeeklyCalendar = ({
  startDate,
  endDate,
  events,
  norms,
  showWeeklyNorm,
  onOpenDetail,
  onOpenCreate,
  onEventDetail,
  loading,
  type,
  eventDetail,
  setEventDetail
}: IWeeklyCalendarProps) => {
  const deviceType = useDeviceType();
  // Tính overlap theo ngày: tạo map eventId -> {count, index} cho mỗi ngày trong tuần
  const overlapMapsByDay = React.useMemo(() => {
    type OverlapInfo = { count: number; index: number }
    const buildClusters = (dayEvents: IEvent[]) => {
      const nonAllDay = dayEvents.filter(
        (e) => !checkIsAllday(dayjs(e.startDate), dayjs(e.endDate))
      )
      if (nonAllDay.length === 0) return [] as IEvent[][]

      const sorted = [...nonAllDay].sort(
        (a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf()
      )

      const clusters: IEvent[][] = []
      let active: IEvent[] = []
      let currentCluster: IEvent[] = []

      sorted.forEach((ev) => {
        const startMs = dayjs(ev.startDate).valueOf()
        active = active.filter((a) => dayjs(a.endDate).valueOf() > startMs)
        if (active.length === 0 && currentCluster.length > 0) {
          clusters.push(currentCluster)
          currentCluster = []
        }
        currentCluster.push(ev)
        active.push(ev)
      })

      if (currentCluster.length > 0) clusters.push(currentCluster)
      return clusters
    }

    const buildOverlapMapForDay = (dayEvents: IEvent[]) => {
      const clusters = buildClusters(dayEvents)
      const result: Record<string, OverlapInfo> = {}

      clusters.forEach((cluster) => {
        // Gán cột cho cluster bằng greedy theo end-time cho từng cột
        const byStart = [...cluster].sort(
          (a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf()
        )
        const columnEndTimes: number[] = []
        const assignments: { id: string; col: number }[] = []

        byStart.forEach((ev) => {
          const start = dayjs(ev.startDate).valueOf()
          const end = dayjs(ev.endDate).valueOf()
          let placed = -1
          for (let c = 0; c < columnEndTimes.length; c++) {
            if (columnEndTimes[c] <= start) {
              placed = c
              columnEndTimes[c] = end
              break
            }
          }
          if (placed === -1) {
            columnEndTimes.push(end)
            placed = columnEndTimes.length - 1
          }
          assignments.push({ id: ev.id, col: placed })
        })

        const totalCols = columnEndTimes.length
        assignments.forEach(({ id, col }) => {
          result[id] = { count: totalCols, index: col }
        })
      })

      return result
    }

    // Tạo 7 map cho 7 ngày
    const maps: Array<Record<string, OverlapInfo>> = []
    for (let i = 0; i < 7; i++) {
      const date = dayjs(startDate).add(i, 'day')
      const dayEvents = events.filter((e) => dayjs(e.startDate).isSame(date, 'day'))
      maps.push(buildOverlapMapForDay(dayEvents))
    }
    return maps
  }, [events, startDate])
  const getTableColumns = () => {
    const { normOfWeek, totalNorm } = calculateNormOfWeek(
      norms,
      events,
      startDate,
      endDate
    )
    // Xác định width cho cột giờ (hour column) theo loại thiết bị
    const HOUR_COLUMN_WIDTH_MOBILE = '52px';
    const HOUR_COLUMN_WIDTH_TABLET_DESKTOP = '70px';
    let hourColumnWidth = HOUR_COLUMN_WIDTH_TABLET_DESKTOP;
    if (deviceType === 'mobile') {
      hourColumnWidth = HOUR_COLUMN_WIDTH_MOBILE;
    }
    const hourColumn: ITableColumn = {
      title: (
        <div className={styles['weekly-calendar__title']}>
          {showWeeklyNorm && (
            <React.Fragment>
              <div>Weekly Norm</div>
              <div
                className={clsx(
                  normOfWeek > totalNorm && styles['!text-error']
                )}
              >{`${normOfWeek}/${totalNorm}`}</div>
            </React.Fragment>
          )}
        </div>
      ),
      dataIndex: 'hour',
      key: 'hour',
      width: hourColumnWidth,
      render: (value: string) => <div>{value}</div>
    }

   const isToday = (index: number) => {
    return dayjs(startDate).add(index, 'day').isSame(dayjs(), 'day');
    }

    // Xác định width cho từng loại thiết bị
    const WEEKLY_CELL_WIDTH_DESKTOP = 'calc((100% - 100px) / 7)';
    const WEEKLY_CELL_WIDTH_TABLET = '152px';
    const WEEKLY_CELL_WIDTH_MOBILE = '90px';
    let weeklyCellWidth = WEEKLY_CELL_WIDTH_DESKTOP;
    if (deviceType === 'tablet') {
      weeklyCellWidth = WEEKLY_CELL_WIDTH_TABLET;
    } else if (deviceType === 'mobile') {
      weeklyCellWidth = WEEKLY_CELL_WIDTH_MOBILE;
    }

  const dayColumns: ITableColumn[] = Object.values(DAYS_OF_WEEK).map(
  (day, index) => ({
    title: (
          <div className={clsx(styles['weekly-calendar__title'], isToday(index) && styles['weekly-calendar__title--active'])}>
            <div className={styles['weekly-calendar__title-day']}>{dayjs(startDate).add(index, 'day').format('DD')}</div>
            <div className={styles['weekly-calendar__title-weekday']}>{day.slice(0, 3)}</div>
      </div>
    ),
    dataIndex: day,
    key: day,
    width: weeklyCellWidth,
    render: (value: ICell) => {
      return (
          <WeeklyCell
            key={index}
            events={value.events}
            date={value.date}
            onOpenDetail={onOpenDetail}
            onOpenCreate={onOpenCreate}
            onEventDetail={onEventDetail}
            eventDetail={eventDetail}
            setEventDetail={setEventDetail}
            overlapMap={overlapMapsByDay[index]}
          />
      )
    }
  })
  )

    return [hourColumn, ...dayColumns]
  }

  const getTableData = () => {
    const data = []

    // All day
    data.push({
      id: -1,
      hour: 'All day',
      ...getEventAllDay(events, startDate)
    })
    const count = type === CALENDAR_TYPE.LMS ? 7 : 0
    // Hour
    for (let i = count; i < HOURS_PER_DAY; i++) {
      const targetHour = dayjs(startDate).add(i, 'hour')

      data.push({
        id: i,
        hour: targetHour.format('HH:mm'),
        ...getEventsOfDay(events, targetHour)
      })
    }

    return data
  }

  return (
  <div className="weekly-calendar-table">
    <Table
      scroll={ deviceType === 'mobile' ? {y: 'calc(100vh - 274px)'} : undefined }
      rowKey={(record) => record.id}
      columns={getTableColumns()}
      dataSource={getTableData()}
      pagination={false}
      rowHoverable={false}
      className={clsx(deviceType === 'mobile' ? styles['weekly-calendar--sm'] : styles['weekly-calendar--md'], styles['weekly-calendar'])}
      loading={loading}
      tableLayout='fixed'
      rowClassName={(record, index) => {
        const data = getTableData();
        return index === data.length - 1 ? 'table-row-last' : '';
      }}
    />
  </div>
  )
}

export default WeeklyCalendar
